import { useRef, useState, useEffect } from 'react'
import { Image } from 'src/components/ui/Image'
import Carousel from 'src/components/restructure/common/Carousel/Carousel'
import { Link } from 'gatsby'
import BestSellerBadge from 'src/components/restructure/product/ProductCard/BestSellerBadge'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import { useMobile } from 'src/hooks/useMobile'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getProductQueryObject } from 'src/utils/amplitude/useProductObject'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { MINI_PDP_EXPERIMENT_ID } from 'src/components/common/StatsigExposure/StatsigExposure'
import { Statsig } from 'src/sdk/statsig'

import ProductFlag from '../ProductCard/ProductFlag'
import type { Product } from '../types/product'
import type { ProductItem } from '../types/productItem'
import { useAddToCartLogic, useSkuSelectionModal } from '../ProductCard/hooks'
import { WishListButton } from '../sections/Sidebar/ProductTitle/WishListButton'
import { ProductAddToCartV3 } from './ProductAddToCartV3'

interface ProductCardHeaderV3Props {
  product: Product
  currentSku: ProductItem
  isBestSellerEligible: boolean
  link: ProductLink
  drag?: boolean
  Images: ProductItem['images']
  isShelfBoughtTogether: boolean
  isSlider?: boolean
  title?: string
  hasFirstImagePriority?: boolean
  customFnOnAdd?: (e: React.MouseEvent<HTMLButtonElement>) => void
  eventSource?: string
}

interface ProductLink {
  to: string
  onClick: () => void
  'data-testid': string
}

type ProductCardCarouselHandle = {
  showSlide: (index: number) => void
}

export default function ProductCardHeaderV3({
  product,
  currentSku,
  isBestSellerEligible,
  link,
  Images,
  isShelfBoughtTogether,
  drag,
  isSlider = false,
  title,
  hasFirstImagePriority = false,
  customFnOnAdd,
  eventSource,
}: ProductCardHeaderV3Props) {
  const showProductFlag = isShelfBoughtTogether || currentSku.flags?.length > 0
  const { pageData } = useGlobalContext()
  const { isMobile } = useMobile()
  const { isLoading } = useCheckout()

  const cartWasClearedRef = useRef(false)
  const carouselRef = useRef<ProductCardCarouselHandle>(null)
  const hoverImageUrl = Images[1]?.imageUrl
  const standardObject = getStandardObject(pageData)
  const productObject = getProductQueryObject(currentSku)
  const isMiniPdpExperimentEnabled = Statsig.useStatsigFeatureGateValue(
    MINI_PDP_EXPERIMENT_ID
  )

  const [canUseDom, setCanUseDom] = useState(false)

  const { addToCartFn } = useAddToCartLogic({
    product,
    currentSku,
    cartWasClearedRef,
    title,
  })

  const { openSkuSelectionModal } = useSkuSelectionModal({
    product,
    currentSku,
    Images,
    isLoading,
    addToCartFn,
    isMobile,
    standardObject,
    productObject,
  })

  const handleHeaderClick = (
    event?: React.MouseEvent | React.KeyboardEvent
  ) => {
    if (!isMiniPdpExperimentEnabled) {
      link.onClick()

      return
    }

    event?.preventDefault()
    event?.stopPropagation()
    link.onClick()
    openSkuSelectionModal()
  }

  useEffect(() => {
    setCanUseDom(true)
  }, [])

  const showSecondImage = () => {
    if (isMobile || !canUseDom || !hoverImageUrl) {
      return
    }

    carouselRef.current?.showSlide(1)
  }

  const showMainImage = () => {
    if (isMobile || !canUseDom || !hoverImageUrl) {
      return
    }

    carouselRef.current?.showSlide(0)
  }

  return (
    <div className="relative mb-[12px]">
      <div
        className="relative aspect-square w-full overflow-hidden bg-restructure-background-neutral-3"
        onMouseEnter={showSecondImage}
        onMouseLeave={showMainImage}
      >
        {isSlider ? (
          <>
            <Link
              onClick={(event) => handleHeaderClick(event)}
              to={link.to}
              className="md:hidden"
            >
              <Image
                src={Images[0].imageUrl}
                alt={product?.productName ?? 'Imagem do carrossel'}
                title={product?.productName ?? Images[0]?.imageText ?? ''}
                width={405}
                height={405}
                loading={hasFirstImagePriority ? 'eager' : 'lazy'}
                fetchpriority={hasFirstImagePriority ? 'high' : 'auto'}
                sizes="(max-width: 768px) 45vw, 405px"
                srcSetUnit="w"
                className="w-full h-full object-contain mix-blend-multiply"
              />
            </Link>
            <Carousel
              ref={carouselRef}
              enableDrag={drag}
              clickCallback={(_, event) => handleHeaderClick(event)}
              href={isMiniPdpExperimentEnabled ? undefined : link.to}
              arrows={false}
              dots={Images.length > 1}
              dotsVariant="product-card"
              items={Images.map((image) => ({
                ...image,
                imageAlt: product.productName,
                imageUrl: image.imageUrl,
              })).slice(0, canUseDom ? Images.length : 1)}
              width={405}
              height={405}
              sizes="(max-width: 768px) 45vw, 405px"
              srcSetUnit="w"
              sectionClassName="hidden md:block h-full w-full"
              hasFirstImagePriority={hasFirstImagePriority}
            />
          </>
        ) : (
          <Carousel
            ref={carouselRef}
            enableDrag={drag}
            clickCallback={(_, event) => handleHeaderClick(event)}
            href={isMiniPdpExperimentEnabled ? undefined : link.to}
            arrows={false}
            dots={Images.length > 1}
            dotsVariant="product-card"
            items={Images.map((image) => ({
              ...image,
              imageAlt: product.productName,
              imageUrl: image.imageUrl,
            })).slice(0, canUseDom ? Images.length : 1)}
            width={405}
            height={405}
            sizes="(max-width: 768px) 45vw, 405px"
            srcSetUnit="w"
            sectionClassName="h-full w-full"
            hasFirstImagePriority={hasFirstImagePriority}
          />
        )}

        <div className="absolute top-2 right-2 text-sm z-10 md:hidden rounded-full p-1">
          <WishListButton
            product={product}
            mobileSize="16"
            desktopSize="24"
            variant="newProductCard"
          />
        </div>
      </div>

      {showProductFlag && (
        <ProductFlag
          flag={currentSku.flags[0]}
          className="absolute top-0 left-0 text-[10px] sm:text-xs p-[6px]"
        />
      )}

      <BestSellerBadge
        isVisible={isBestSellerEligible}
        hasProductFlag={showProductFlag}
      />

      <div className="hidden absolute top-3 right-3 text-sm z-10 md:block rounded-full p-1">
        <WishListButton
          product={product}
          mobileSize="16"
          desktopSize="24"
          variant="newProductCard"
        />
      </div>
      <ProductAddToCartV3
        currentSku={currentSku}
        product={product}
        Images={Images}
        title={title}
        customFnOnAdd={customFnOnAdd}
        eventSource={eventSource}
      />
    </div>
  )
}
